<template>
  <section class="financial-transfers-tab">
    <FinancialTransfersToolbar
      :labels="text.transfersTab.toolbar"
      :account-options="toolbarAccountOptions"
      @search-change="handleToolbarSearchChange"
      @filters-change="handleToolbarFiltersChange"
      @apply="handleToolbarApply"
    />

    <div class="app-table-wrap">
      <div class="app-table-scroll">
        <table class="app-data-table">
          <thead>
            <tr>
              <th class="app-col-index">{{ text.transfersTab.table.headers.index }}</th>
              <th>{{ text.transfersTab.table.headers.date }}</th>
              <th>{{ text.transfersTab.table.headers.sourceAccount }}</th>
              <th>{{ text.transfersTab.table.headers.targetAccount }}</th>
              <th>{{ text.transfersTab.table.headers.concept }}</th>
              <th class="app-text-right">{{ text.transfersTab.table.headers.sourceAmount }}</th>
              <th class="app-text-right">{{ text.transfersTab.table.headers.targetAmount }}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td colspan="8" class="app-table-empty">{{ text.transfersTab.table.loading }}</td>
            </tr>

            <tr v-else-if="filteredRows.length === 0">
              <td colspan="8" class="app-table-empty">{{ text.transfersTab.table.empty }}</td>
            </tr>

            <tr v-else v-for="(row, index) in filteredRows" :key="row.id">
              <td class="app-muted">{{ index + 1 }}</td>
              <td>{{ formatTableDate(row.date) }}</td>
              <td>
                <a
                  class="financial-transfers-account-link app-strong"
                  :href="resolveAccountMovementsHref(row.financial_account_source_id)"
                  @click.prevent="openAccountMovements(row.financial_account_source_id)"
                >
                  {{ row.source_account_name }}
                </a>
              </td>
              <td>
                <a
                  class="financial-transfers-account-link app-strong"
                  :href="resolveAccountMovementsHref(row.financial_account_target_id)"
                  @click.prevent="openAccountMovements(row.financial_account_target_id)"
                >
                  {{ row.target_account_name }}
                </a>
              </td>
              <td>{{ row.description }}</td>
              <td class="app-text-right app-strong">{{ formatAmount(row.source_amount, row.source_currency) }}</td>
              <td class="app-text-right app-strong">{{ formatAmount(row.target_amount, row.target_currency) }}</td>
              <td class="app-table-actions-cell">
                <div class="app-table-actions-inline">
                  <button
                    type="button"
                    class="app-table-icon-btn app-table-icon-btn--edit"
                    :title="text.transfersTab.table.actions.edit"
                    @click="openEditModal(row)"
                  >
                    <i class="fa fa-pencil" aria-hidden="true"></i>
                  </button>

                  <button
                    type="button"
                    class="app-table-icon-btn app-table-icon-btn--delete"
                    :title="text.transfersTab.table.actions.delete"
                    @click="openDeleteConfirm(row)"
                  >
                    <i class="fa fa-trash" aria-hidden="true"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <FinancialTransferModal
      :show="isTransferModalOpen"
      :loading="saving"
      :mode="transferModalMode"
      :text="text.transfersTab"
      :accounts="accounts"
      :initial-data="editingTransfer"
      @close="closeTransferModal"
      @submit="submitTransfer"
    />

    <ConfirmActionModal
      :show="isConfirmDeleteOpen"
      tone="danger"
      :loading="deleting"
      :title="text.transfersTab.confirmDelete.title"
      :subtitle="text.transfersTab.confirmDelete.subtitle"
      :message="confirmDeleteMessage"
      :confirm-text="text.transfersTab.confirmDelete.confirm"
      :cancel-text="text.transfersTab.actions.cancel"
      @confirm="confirmDelete"
      @cancel="closeDeleteConfirm"
    />
  </section>
</template>

<script>
import FinancialTransfersToolbar from '@/components/financial/FinancialTransfersToolbar.vue';
import ConfirmActionModal from '@/components/global/ConfirmActionModal.vue';
import FinancialTransferModal from '@/components/financial/modals/FinancialTransferModal.vue';
import financialAccountsService from '@/services/financialAccounts';
import financialTransfersService from '@/services/financialTransfers';
import { extractPayload } from '@/utils/dataNormalization';
import { buildCurrentMonthApiRange, formatDateShort } from '@/utils/datePresentation';
import { formatCurrencyDisplay } from '@/utils/numberFormat';
import { buildComparableHaystack, toComparableSearchText } from '@/utils/textValue';

export default {
  name: 'FinancialTransfersTab',
  components: {
    FinancialTransfersToolbar,
    ConfirmActionModal,
    FinancialTransferModal,
  },
  props: {
    text: {
      type: Object,
      required: true,
    },
    openCreateSignal: {
      type: Number,
      default: 0,
    },
  },
  emits: ['updated', 'modal-closed'],
  data() {
    return {
      loading: false,
      saving: false,
      deleting: false,
      search: '',
      activeFilters: {
        ...buildCurrentMonthApiRange(),
        financial_account_source_id: '',
        financial_account_target_id: '',
      },
      lastFetchedFiltersSignature: '',
      rows: [],
      accounts: [],
      isTransferModalOpen: false,
      transferModalMode: 'create',
      editingTransfer: null,
      isConfirmDeleteOpen: false,
      deletingTransfer: null,
    };
  },
  computed: {
    filteredRows() {
      const source = Array.isArray(this.rows) ? this.rows : [];
      const term = toComparableSearchText(this.search);
      if (term === '') {
        return source;
      }

      return source.filter((row) => {
        const haystack = buildComparableHaystack([
          row?.id,
          row?.date,
          row?.description,
          row?.source_account_name,
          row?.target_account_name,
          row?.source_amount,
          row?.target_amount,
          row?.source_currency,
          row?.target_currency,
          this.formatAmount(row?.source_amount, row?.source_currency),
          this.formatAmount(row?.target_amount, row?.target_currency),
        ]);

        return haystack.includes(term);
      });
    },
    toolbarAccountOptions() {
      return (Array.isArray(this.accounts) ? this.accounts : [])
        .map((account) => {
          const id = Number(account?.id || 0);
          if (id <= 0) {
            return null;
          }

          const name = String(account?.name || '').trim();
          const currency = String(account?.currency || '').trim().toUpperCase();
          return {
            value: id,
            label: currency ? `${name} (${currency})` : name,
          };
        })
        .filter(Boolean);
    },
    confirmDeleteMessage() {
      const source = String(this.deletingTransfer?.source_account_name || '').trim();
      const target = String(this.deletingTransfer?.target_account_name || '').trim();

      if (!source && !target) {
        return this.text.transfersTab.confirmDelete.message;
      }

      return `${this.text.transfersTab.confirmDelete.message}\n\n${source} -> ${target}`;
    },
  },
  watch: {
    openCreateSignal() {
      this.openCreateModal();
    },
  },
  created() {
    this.bootstrap();
  },
  methods: {
    async bootstrap() {
      await this.loadAccountOptions();
      await this.fetchTransfers(this.activeFilters);
    },
    async loadAccountOptions() {
      try {
        const response = await financialAccountsService.list({ status: 'all' }, { skipLoading: true });
        const payload = extractPayload(response);
        const rows = Array.isArray(payload?.rows) ? payload.rows : [];

        this.accounts = rows.map((row) => ({
          id: Number(row?.id || 0),
          name: String(row?.name || '').trim(),
          currency: String(row?.currency || 'MXN').trim().toUpperCase() || 'MXN',
        }));
      } catch (error) {
        this.accounts = [];
      }
    },
    async fetchTransfers(filters = {}) {
      this.loading = true;

      try {
        const response = await financialTransfersService.list(filters || {}, { skipLoading: true });
        const payload = extractPayload(response);
        const rows = Array.isArray(payload?.rows) ? payload.rows : [];

        this.rows = rows.map((row) => ({
          id: Number(row?.id || 0),
          date: String(row?.date || '').trim(),
          description: String(row?.description || '').trim(),
          financial_account_source_id: Number(row?.financial_account_source_id || 0),
          financial_account_target_id: Number(row?.financial_account_target_id || 0),
          source_account_name: String(row?.source_account_name || '').trim(),
          target_account_name: String(row?.target_account_name || '').trim(),
          source_amount: Number(row?.source_amount || 0),
          target_amount: Number(row?.target_amount || 0),
          source_currency: String(row?.source_currency || 'MXN').trim().toUpperCase() || 'MXN',
          target_currency: String(row?.target_currency || 'MXN').trim().toUpperCase() || 'MXN',
          exchange_rate: Number(row?.exchange_rate || 1) || 1,
        }));

        this.lastFetchedFiltersSignature = this.buildFiltersSignature(this.activeFilters);
      } catch (error) {
        this.rows = [];
        const message = error?.response?.data?.message || this.text.transfersTab.toast.loadError;
        this.$toast?.error(message);
      } finally {
        this.loading = false;
      }
    },
    async applyFilters() {
      await this.fetchTransfers(this.activeFilters);
    },
    handleToolbarSearchChange(value) {
      this.search = String(value || '');
    },
    handleToolbarFiltersChange(filters) {
      this.activeFilters = {
        date_from: String(filters?.date_from || '').trim(),
        date_to: String(filters?.date_to || '').trim(),
        financial_account_source_id: Number(filters?.financial_account_source_id || 0) > 0
          ? Number(filters.financial_account_source_id)
          : '',
        financial_account_target_id: Number(filters?.financial_account_target_id || 0) > 0
          ? Number(filters.financial_account_target_id)
          : '',
      };
    },
    async handleToolbarApply(payload) {
      this.handleToolbarSearchChange(payload?.search);
      this.handleToolbarFiltersChange(payload?.filters || {});
      const nextFiltersSignature = this.buildFiltersSignature(this.activeFilters);

      if (nextFiltersSignature !== this.lastFetchedFiltersSignature) {
        await this.applyFilters();
      }
    },
    buildFiltersSignature(filters = {}) {
      return JSON.stringify({
        date_from: String(filters?.date_from || '').trim(),
        date_to: String(filters?.date_to || '').trim(),
        financial_account_source_id: Number(filters?.financial_account_source_id || 0) > 0
          ? Number(filters.financial_account_source_id)
          : '',
        financial_account_target_id: Number(filters?.financial_account_target_id || 0) > 0
          ? Number(filters.financial_account_target_id)
          : '',
      });
    },
    formatAmount(value, currency) {
      return formatCurrencyDisplay(Number(value || 0), {
        currency: String(currency || 'MXN').trim().toUpperCase() || 'MXN',
      });
    },
    formatTableDate(value) {
      return formatDateShort(value, { fallback: '-' });
    },
    resolveAccountMovementsHref(financialAccountId) {
      const accountId = Number(financialAccountId || 0);
      if (accountId <= 0 || !this.$router) {
        return '#';
      }

      return this.$router.resolve({
        name: 'financialAccountDetail',
        params: { financialAccountId: String(accountId) },
        query: { tab: 'movements' },
      }).href;
    },
    openAccountMovements(financialAccountId) {
      const accountId = Number(financialAccountId || 0);
      if (accountId <= 0) {
        return;
      }

      this.$router.push({
        name: 'financialAccountDetail',
        params: { financialAccountId: String(accountId) },
        query: { tab: 'movements' },
      });
    },
    openCreateModal() {
      this.transferModalMode = 'create';
      this.editingTransfer = null;
      this.isTransferModalOpen = true;
    },
    openEditModal(row) {
      this.transferModalMode = 'edit';
      this.editingTransfer = { ...(row || {}) };
      this.isTransferModalOpen = true;
    },
    closeTransferModal(force = false) {
      if (this.saving && !force) {
        return;
      }

      this.isTransferModalOpen = false;
      this.transferModalMode = 'create';
      this.editingTransfer = null;
      this.$emit('modal-closed');
    },
    async submitTransfer(payload) {
      if (this.saving) {
        return;
      }

      this.saving = true;

      try {
        if (this.transferModalMode === 'edit' && Number(this.editingTransfer?.id || 0) > 0) {
          const response = await financialTransfersService.update(Number(this.editingTransfer.id), payload || {});
          this.$toast?.success(response?.message || this.text.transfersTab.toast.updateSuccess);
          this.closeTransferModal(true);
          await this.fetchTransfers(this.activeFilters);
          this.$emit('updated', { action: 'updated' });
        } else {
          const response = await financialTransfersService.store(payload || {});
          this.$toast?.success(response?.message || this.text.transfersTab.toast.createSuccess);
          this.closeTransferModal(true);
          await this.fetchTransfers(this.activeFilters);
          this.$emit('updated', { action: 'created' });
        }
      } catch (error) {
        const fallback = this.transferModalMode === 'edit'
          ? this.text.transfersTab.toast.updateError
          : this.text.transfersTab.toast.createError;
        const message = error?.response?.data?.message || fallback;
        this.$toast?.error(message);
      } finally {
        this.saving = false;
      }
    },
    openDeleteConfirm(row) {
      this.deletingTransfer = row || null;
      this.isConfirmDeleteOpen = true;
    },
    closeDeleteConfirm(force = false) {
      if (this.deleting && !force) {
        return;
      }

      this.isConfirmDeleteOpen = false;
      this.deletingTransfer = null;
    },
    async confirmDelete() {
      const transferId = Number(this.deletingTransfer?.id || 0);
      if (transferId <= 0 || this.deleting) {
        this.closeDeleteConfirm();
        return;
      }

      this.deleting = true;

      try {
        const response = await financialTransfersService.delete(transferId);
        this.$toast?.success(response?.message || this.text.transfersTab.toast.deleteSuccess);
        this.closeDeleteConfirm(true);
        await this.fetchTransfers(this.activeFilters);
        this.$emit('updated', { action: 'deleted' });
      } catch (error) {
        const message = error?.response?.data?.message || this.text.transfersTab.toast.deleteError;
        this.$toast?.error(message);
      } finally {
        this.deleting = false;
      }
    },
  },
};
</script>

<style scoped>
.financial-transfers-tab {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.financial-transfers-account-link {
  color: inherit;
  text-decoration: none;
}

.financial-transfers-account-link:hover {
  color: #0b63b4;
  text-decoration: underline;
}
</style>
